import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  competitionDetailSchema,
  competitionListSchema,
  type Competition,
  type CompetitionStatus,
} from '@competa/shared';
import { ApiRequestError, apiRequest } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import dialogStyles from '../../components/Dialog.module.css';
import { TextField } from '../../components/TextField';
import { errorMessage, fieldErrors } from '../../i18n/errors';
import styles from './Competitions.module.css';

/** Farbton der Statusmarke. */
const STATUS_TONE: Record<CompetitionStatus, 'neutral' | 'warning' | 'success'> = {
  open: 'neutral',
  results_entered: 'warning',
  closed: 'success',
};

/**
 * Statusmarke einer Teilnahme.
 * @param props Status.
 */
export function StatusBadge({ status }: { status: CompetitionStatus }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`competitions.status.${status}`)}</Badge>;
}

/**
 * Dialog zum manuellen Anlegen einer Teilnahme (ohne Eintrag im Wettbewerbskatalog).
 * @param props Sichtbarkeit und Rückrufe.
 */
function ManualDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [fiapNumber, setFiapNumber] = useState('');

  const create = useMutation({
    mutationFn: () =>
      apiRequest('POST', '/competitions', {
        body: {
          name,
          country: country || null,
          fiapNumber: fiapNumber || null,
        },
        schema: competitionDetailSchema,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['competitions'] });
      navigate(`/competitions/${created.id}`);
    },
  });

  /** Sendet das Formular. */
  function submit(event: FormEvent): void {
    event.preventDefault();
    create.mutate();
  }

  const fields = fieldErrors(t, create.error);
  return (
    <Dialog title={t('competitions.manualTitle')} isOpen={isOpen} onClose={onClose}>
      <form onSubmit={submit}>
        <TextField
          label={t('competitions.name')}
          value={name}
          required
          error={fields.name}
          onChange={(event) => setName(event.target.value)}
        />
        <TextField
          label={t('competitions.country')}
          value={country}
          error={fields.country}
          onChange={(event) => setCountry(event.target.value)}
        />
        <TextField
          label={t('competitions.fiapNumber')}
          value={fiapNumber}
          placeholder="2026/123"
          error={fields.fiapNumber}
          onChange={(event) => setFiapNumber(event.target.value)}
        />
        {create.error && !Object.keys(fields).length && (
          <p role="alert" className={styles.error}>
            {errorMessage(t, create.error)}
          </p>
        )}
        <div className={dialogStyles.actions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {t('common.create')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Zeile der Teilnahmeliste.
 * @param props Teilnahme.
 */
function CompetitionItem({ competition }: { competition: Competition }) {
  const { t } = useTranslation();
  const { stats } = competition;
  const successes = stats.acceptance + stats.honorableMention + stats.medal;
  return (
    <li className={styles.card}>
      <div>
        <Link to={`/competitions/${competition.id}`} className={styles.cardTitle}>
          {competition.name}
        </Link>
        <div className={styles.meta}>
          {competition.fiapNumber && <span>{competition.fiapNumber}</span>}
          {competition.country && <span>{competition.country}</span>}
          <span>{t('competitions.images', { count: stats.total })}</span>
          <span>{t('competitions.successes', { count: successes })}</span>
        </div>
      </div>
      <StatusBadge status={competition.status} />
    </li>
  );
}

/**
 * Liste der eigenen Teilnahmen mit Textsuche und Statusfilter; neue Teilnahmen entstehen über die
 * Wettbewerbssuche oder manuell.
 */
export function CompetitionsPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<CompetitionStatus | ''>('');
  const [manual, setManual] = useState(false);

  const search = new URLSearchParams({ limit: '200' });
  if (q) search.set('q', q);
  if (status) search.set('status', status);
  const list = useQuery({
    queryKey: ['competitions', search.toString()],
    queryFn: () =>
      apiRequest('GET', `/competitions?${search.toString()}`, { schema: competitionListSchema }),
    placeholderData: keepPreviousData,
  });

  return (
    <section>
      <div className={styles.header}>
        <h1>{t('competitions.title')}</h1>
        <div className={styles.actions}>
          <Link to="/competitions/new">{t('competitions.fromCatalog')}</Link>
          <Button variant="secondary" onClick={() => setManual(true)}>
            {t('competitions.manual')}
          </Button>
        </div>
      </div>
      <div className={styles.toolbar} role="search">
        <TextField
          label={t('competitions.search')}
          type="search"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        <div className={styles.selectField}>
          <label htmlFor="filter-status">{t('competitions.statusLabel')}</label>
          <select
            id="filter-status"
            className={styles.select}
            value={status}
            onChange={(event) => setStatus(event.target.value as CompetitionStatus | '')}
          >
            <option value="">{t('competitions.anyStatus')}</option>
            {(['open', 'results_entered', 'closed'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`competitions.status.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {list.isPending && <p role="status">{t('common.loading')}</p>}
      {list.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, list.error)}
        </p>
      )}
      {list.data && (
        <>
          <p role="status">
            {list.data.total === 0
              ? t('competitions.empty')
              : t('competitions.count', { count: list.data.total })}
          </p>
          <ul className={styles.list}>
            {list.data.items.map((competition) => (
              <CompetitionItem key={competition.id} competition={competition} />
            ))}
          </ul>
        </>
      )}
      <ManualDialog isOpen={manual} onClose={() => setManual(false)} />
    </section>
  );
}

/**
 * Prüft, ob ein Fehler „Teilnahme besteht bereits" ist, und liefert dann deren ID.
 * @param error Aufgetretener Fehler.
 * @returns ID der vorhandenen Teilnahme oder `null`.
 */
export function existingCompetitionId(error: unknown): number | null {
  if (error instanceof ApiRequestError && error.code === 'competition_exists') {
    const id = Number(error.fields?.existingId);
    return Number.isInteger(id) && id > 0 ? id : null;
  }
  return null;
}
